import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { 
  Calendar,
  Share2,
  Loader2,
  AlertTriangle,
  Copy,
  ExternalLink
} from "lucide-react";
import { type Task } from "@shared/schema";
import { WeeklyCalendar } from "@/components/ui/weekly-calendar";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";

export default function WeeklyPlanner() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { toast } = useToast();
  const [location] = useLocation();
  const [shareLink, setShareLink] = useState("");
  const [animate, setAnimate] = useState(false);

  const { data: tasks, isLoading, error } = useQuery<Task[]>({
    queryKey: ["/api/tasks"],
  });

  // Filtrujeme vyfaktúrované úlohy z označených hviezdičkou
  const starredTasks = tasks?.filter(task => task.isStarred && !task.isInvoiced) || [];
  
  // Logovanie úloh pre ladenie
  useEffect(() => {
    if (tasks) {
      // Task filtering for starred and uninvoiced items
    }
  }, [tasks, starredTasks]);
  
  // Aktivácia animácií po načítaní komponentu
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimate(true);
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);

  // Vygenerovanie odkazu pre zdieľanie
  useEffect(() => {
    const protocol = window.location.protocol;
    const host = window.location.host;
    setShareLink(`${protocol}//${host}/standalone-planner`);
  }, []);

  // Funkcia pre kopírovanie odkazu do schránky
  const copyLinkToClipboard = () => {
    navigator.clipboard.writeText(shareLink).then(() => {
      toast({
        title: "Odkaz skopírovaný",
        description: "Odkaz na týždenný plánovač bol skopírovaný do schránky",
      });
    }).catch(err => {
      console.error('Nepodarilo sa skopírovať odkaz:', err);
      toast({
        title: "Chyba pri kopírovaní",
        description: "Nepodarilo sa skopírovať odkaz do schránky",
        variant: "default",
      });
    });
  };

  // Funkcia pre otvorenie odkazu v novom okne
  const openInNewWindow = () => {
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const width = Math.min(1400, screenWidth * 0.9);
    const height = Math.min(900, screenHeight * 0.9);
    const left = (screenWidth - width) / 2;
    const top = (screenHeight - height) / 2;
    
    window.open(
      shareLink, 
      '_blank', 
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no`
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-destructive">
        <AlertTriangle className="h-5 w-5" />
        <p>Chyba pri načítaní údajov</p>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 section-spacing">
        <h1 className="page-title text-2xl sm:text-3xl font-bold">Týždenný plánovač</h1>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <Button 
            variant="outline" 
            size="sm" 
            className="gap-2 flex-1 sm:flex-initial min-w-0"
            onClick={copyLinkToClipboard}
          >
            <Copy className="h-4 w-4 shrink-0" />
            <span className="truncate">Kopírovať odkaz</span>
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            className="gap-2 flex-1 sm:flex-initial min-w-0"
            onClick={openInNewWindow}
          >
            <ExternalLink className="h-4 w-4 shrink-0" />
            <span className="hidden xs:inline">Otvoriť v novom okne</span>
            <span className="xs:hidden">Nové okno</span>
          </Button>
        </div>
      </div>

      <Card className="mb-6 sm:mb-8 min-w-0 overflow-hidden">
        <CardHeader className="pb-2 px-3 sm:px-6">
          <CardTitle className="text-base sm:text-lg font-medium flex items-center">
            <Calendar className="h-5 w-5 mr-2 text-primary" />
            Týždenný plánovač úloh
          </CardTitle>
          <CardDescription>
            Drag-and-drop organizácia úloh na týždňový prehľad (nemení skutočné dátumy úloh)
          </CardDescription>
        </CardHeader>
        <CardContent className="px-3 sm:px-6 overflow-x-auto">
          <WeeklyCalendar tasks={starredTasks} />
        </CardContent>
        <CardFooter className="text-xs sm:text-sm text-muted-foreground pt-2 border-t px-3 sm:px-6">
          <p>Zobrazujú sa iba označené úlohy označené hviezdičkou. Zmeny v usporiadaní úloh nemenía skutočné dátumy.</p>
        </CardFooter>
      </Card>
    </div>
  );
}